import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';

import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import CoverPage from './coverPage';
import DisclaimerPage from './disclaimerPage';
import MethodologySection from './methodologyPage';
import ExecutiveReportWebPentest from './executiveReport';
import WhiteLabelForm from '../../../components/whiteLabelForm';
import Nav from '../nav';

import './ReportsPage.css';

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const SEVERITY_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };
const severityColors = {
  critical: '#800080',
  high: '#f44336',
  medium: '#ff9800',
  low: '#2196f3',
  info: '#4caf50',
  default: '#9e9e9e',
};

const severityColor = (severity) => severityColors[severity?.toLowerCase?.()] || severityColors.default;

const normalizeSeverity = (severity) => {
  if (!severity) return 'Medium';

  const normalized = String(severity).trim().toLowerCase();
  if (normalized.startsWith('crit')) return 'Critical';
  if (normalized.startsWith('high')) return 'High';
  if (normalized.startsWith('med')) return 'Medium';
  if (normalized.startsWith('low')) return 'Low';
  if (normalized.startsWith('info') || normalized.startsWith('inform')) return 'Info';
  return 'Medium';
};

const extractSeverity = (value) => {
  if (!value) return 'Medium';

  if (typeof value === 'object') {
    if (value.severity) return normalizeSeverity(value.severity);
    if (value.report_details) return extractSeverity(value.report_details);
    return 'Medium';
  }

  if (typeof value === 'string') {
    const match = value.match(/(?:^|\n)#{1,3}\s*Severity[\s:]*\n?\s*(Critical|High|Medium|Low|Info)\b/i);
    if (match?.[1]) return normalizeSeverity(match[1]);
  }

  return 'Medium';
};

const cleanReportDetails = (value = '') =>
  String(value).replace(/##\s*Severity[\s\S]*?\n{2,}/i, '').trim();

const SeverityChip = ({ label }) => (
  <Chip
    label={label}
    sx={{
      mb: 1,
      px: 2,
      fontWeight: 600,
      backgroundColor: severityColor(label),
      color: '#fff',
    }}
  />
);

const TableOfContents = ({ items = [] }) => {
  const slugify = (txt) =>
    (txt || '')
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');

  const sections = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    { id: 'findings', title: 'Findings', level: 1 },
    ...items.map((item, index) => ({
      id: item?.name ? slugify(item.name) : `finding-${index + 1}`,
      title: item?.name || `Finding ${index + 1}`,
      level: 2,
    })),
  ];

  return (
    <Box sx={{ p: '0.8in 0.9in' }} id="table-of-contents">
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Table of Contents
      </Typography>

      <Box className="toc-wrapper">
        {sections.map((item) => (
          <Box
            key={item.id}
            component="a"
            href={`#${item.id}`}
            className={`toc-row ${item.level === 2 ? 'toc-sub' : ''}`}
          >
            <span className="toc-title">{item.title}</span>
            <span className="toc-dots" />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const ReportsPage = () => {
  const [exploitsData, setExploitsData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client !== undefined) setClientName(client);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${BASEURL}/pentests/web/exploits?pentest_id=${pentest_id}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') {
          navigate('/');
          return;
        }

        setExploitsData((data?.exploits || []).filter((item) => item && item.name));
      })
      .catch(console.error);
  }, [navigate, pentest_id]);

  const exploits = useMemo(() => (
    [...exploitsData]
      .map((item) => ({
        ...item,
        severity: extractSeverity(item),
        cleanedDetails: cleanReportDetails(item.report_details),
      }))
      .sort((left, right) => (SEVERITY_RANK[left.severity] ?? 999) - (SEVERITY_RANK[right.severity] ?? 999))
  ), [exploitsData]);

  const handleDownloadPDF = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setDownloading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, exploitsRes, summaryRes] = await Promise.all([
        fetch(`${BASEURL}/pentests/web/stats?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
        fetch(`${BASEURL}/pentests/web/exploits?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
        fetch(`${BASEURL}/pentest/report/executive?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
      ]);

      if ([statsRes, exploitsRes, summaryRes].some((result) => result?.message === 'Invalid token')) {
        navigate('/');
        return;
      }

      const [
        { pdf },
        { default: WebReportPdfDocument },
        { buildWebReportPdfData },
      ] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./webReportPdf'),
        import('./webReportPdfHelpers'),
      ]);

      const reportData = buildWebReportPdfData({
        pentestId: pentest_id,
        primaryColor,
        companyName,
        clientName,
        stats: statsRes?.stats || statsRes,
        exploits: exploitsRes?.exploits || exploitsData,
        executiveSummary: summaryRes?.report || '',
      });

      const blob = await pdf(<WebReportPdfDocument report={reportData} />).toBlob();
      downloadBlob(blob, `web_${pentest_id}_report.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Backdrop open={downloading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ p: 2, '& > [aria-label^="pdf-page-"]': { mb: 2.5 } }}>
        <Box className="pdf-page cover-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-0" data-report-section="cover">
          <Card
            className="report-page-card report-page-card--cover"
            sx={{
              width: '100%',
              maxWidth: '8.5in',
              height: '10.8in',
              m: 0,
              p: 0,
              border: 0,
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <CoverPage primary={primaryColor} company={companyName} clientName={clientName} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-1" data-report-section="disclaimer">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <DisclaimerPage primary={primaryColor} company={companyName} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-2" data-report-section="toc">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <CardContent>
              <TableOfContents items={exploits} />
            </CardContent>
          </Card>
        </Box>

        <Box className="pdf-page exec-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-3" data-report-section="executive">
          <Card className="report-page-card report-page-card--standard exec-wrapper allow-split" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <CardContent id="executive-summary" className="exec-embed allow-split">
              <ExecutiveReportWebPentest minimal={true} primary={primaryColor} company={companyName} client={clientName} />
            </CardContent>
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-4" data-report-section="methodology">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <MethodologySection primary={primaryColor} />
          </Card>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography className="report-section-heading" variant="h4" id="findings" sx={{ my: 3, textAlign: 'center' }}>
            Findings
          </Typography>
        </Box>

        {exploits.map((item, index) => {
          const id = item.name.toLowerCase().replace(/\s+/g, '-');
          const pageNumber = index + 5;

          return (
            <Box key={id} className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label={`pdf-page-${pageNumber}`} data-report-section="finding">
              <Card className="report-page-card report-page-card--standard finding-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2 }}>
                <CardContent className="finding-card__content">
                  <Box className="finding-header">
                    <Typography id={id} variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {`${index + 1}. ${item.name}`}
                    </Typography>
                    <SeverityChip label={item.severity || 'Medium'} />
                    {item.impacted_assets ? (
                      <Typography variant="body1" gutterBottom>
                        <strong>Endpoint:</strong> {item.impacted_assets}
                      </Typography>
                    ) : null}
                    <Divider sx={{ my: 2 }} />
                  </Box>

                  <Box className="finding-markdown">
                    <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                      {item.cleanedDetails || 'No additional details provided.'}
                    </ReactMarkdown>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default ReportsPage;
